import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { dsClass } from '../../../../common/constants/dsClasses';
import { paths } from '../../../../common/constants/pathVariables.js';
import { finalizeShoppingBasket } from '../../../../common/fetch.js';
import {
  getShoppingBasketIdFromLocalStorage,
  parseBasketJson,
  useShoppingBasket,
} from '../../../../common/hooks/useShoppingBasket';
import { useVoucher } from '../../../../common/hooks/useVoucher';
import { orderHistoryMsg, productsMsg, returnToTheShopMsg, t } from '../../../../common/i18n/index.js';
import { formatSumToString } from '../../../../common/utils/priceUtils';
import { getContactInfo } from '../../../../common/utils/stateUtils';
import { DeliveryType } from '../../../../generated/api/deliveryType';
import { useAuth } from '../../../../public/site/AuthProvider';
import { useOnlineModelsContext } from '../../../../public/site/onlineModel/onlineModelContextProvider';
import {
  areAllModelCodesLoaded,
  expandRelatedModelCodes,
  getOnlineModelsWithVoucher,
} from '../../../../public/site/onlineModel/onlineModelService';

import { PaymentTypeEnum } from '../../../../selfservice/common/googleEcommerceEvent';
import { clearShoppingBasketFromLocalstorage } from '../../../../selfservice/common/localStorageUtils';
import { Grid } from '../../../Grid/Grid.js';
import { Loading } from '../../../Loading';
import { OrderNewsLetter } from '../../../OrderNewsLetter/OrderNewsLetter';
import { getOfferForBasketItem, getOnlineModelForBasketItem } from '../../../ShoppingBasket/shoppingBasketUtils';
import { useModelsForBasket } from '../../hooks/useModelsForBasket';
import {
  type StoredPlaceOrderCardPaymentData,
  clearFormDataFromSession,
  getFormDataFromSession,
} from '../../utils/cardPaymentUtils.js';
import { handleOrderPurchaseForPlaceOrder } from '../../utils/placeOrderAnalyticsUtils';
import {
  getAddonsForBasketItem,
  getContactInfoLines,
  getDeliveryFields,
  getDeliveryMethodEntry,
} from '../../utils/placeOrderUtils';
import { OrderInfo } from '../payment/OrderInfo';
import { ProductEntry, ProductEntryView } from '../products/ProductEntry';
import { ThankYouSummary } from './ThankYouSummary';
import type { BasketItem, ShoppingBasketType } from '../../../../common/types/shoppingBasket';
import type { CampaignData } from '../../placeOrderTypes';

import './PlaceOrderThankYou.scss';

/**
 * PlaceOrderThankYou - Thank you page after successful order
 *
 * This component is shown after:
 * - Successful card payment (after finalize API returns 201/202)
 * - Successful billing account order (after order API returns 201/202)
 *
 * For card payments, it reads form data from session storage to display order details.
 * Session storage is cleared after the page has fully rendered.
 */
export const PlaceOrderThankYou = () => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<StoredPlaceOrderCardPaymentData | null>(null);
  const [preservedBasketItems, setPreservedBasketItems] = useState<BasketItem[] | null>(null);
  const [analyticsTracked, setAnalyticsTracked] = useState(false);
  const { isAuthenticated, isLoading, authenticatedUser } = useAuth();
  const { shoppingBasket: shoppingBasketJson } = useShoppingBasket();
  const initialShoppingBasket: ShoppingBasketType = parseBasketJson(shoppingBasketJson);
  const { priceAlteredModels, fetchModelsByCodes, activeVoucherCode, setActiveVoucher, loadVoucherModels } =
    useOnlineModelsContext();

  // Use preserved basket items if available, otherwise use current basket
  const shoppingBasket: ShoppingBasketType = preservedBasketItems
    ? { ...initialShoppingBasket, items: preservedBasketItems }
    : initialShoppingBasket;

  const onlineModelCodeGuids = [...new Set((shoppingBasket.items ?? []).map(cartItem => cartItem.guid))];
  const expandedGuids = expandRelatedModelCodes(onlineModelCodeGuids);
  const privateOnlineModels = useModelsForBasket(shoppingBasket);
  const voucherOnlineModels = activeVoucherCode
    ? getOnlineModelsWithVoucher(expandedGuids, activeVoucherCode, priceAlteredModels.onlineModelsByVoucher)
    : undefined;

  const voucherHook = useVoucher(
    loadVoucherModels,
    setActiveVoucher,
    fetchModelsByCodes,
    shoppingBasket,
    activeVoucherCode,
    voucherOnlineModels
  );

  const { campaignsFromVoucher, campaignContextsFromVoucher } = voucherHook;

  const campaignData = activeVoucherCode
    ? ({ voucherCode: activeVoucherCode, campaignsFromVoucher, campaignContextsFromVoucher } as CampaignData)
    : undefined;

  // Read session data and preserve basket items on mount
  useEffect(() => {
    const data = getFormDataFromSession();
    if (data) {
      setSessionData(data);
    }
    // Preserve basket items before any cleanup happens
    if (initialShoppingBasket.items && initialShoppingBasket.items.length > 0) {
      setPreservedBasketItems(initialShoppingBasket.items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount to capture initial state
  }, []);

  // Clear storage only after the page has rendered with the data and items are preserved
  useEffect(() => {
    if (sessionData && preservedBasketItems && preservedBasketItems.length > 0) {
      // Get shopping cart ID and order ID before clearing
      const shoppingCartId = getShoppingBasketIdFromLocalStorage();
      const orderId = sessionData?.orderId;

      clearFormDataFromSession();

      clearShoppingBasketFromLocalstorage();

      // Finalize shopping cart in broker
      if (shoppingCartId && orderId) {
        finalizeShoppingBasket(shoppingCartId, orderId).catch(_error => {
          // Silently fail, don't block the user experience
        });
      }
    }
  }, [sessionData, preservedBasketItems]);

  const handleReturnToShop = () => {
    navigate(paths.WEB_SHOP);
  };

  const onlineModels = voucherOnlineModels ?? privateOnlineModels;

  const allExpandedModelsLoaded = areAllModelCodesLoaded(expandedGuids, onlineModels);

  // Track purchase analytics once all data is ready
  useEffect(() => {
    if (
      sessionData &&
      preservedBasketItems &&
      preservedBasketItems.length > 0 &&
      allExpandedModelsLoaded &&
      !analyticsTracked
    ) {
      const analyticsPaymentType =
        sessionData.formData.payment.paymentType === 'CARD' ? PaymentTypeEnum.CREDIT_CARD : PaymentTypeEnum.INVOICE;
      handleOrderPurchaseForPlaceOrder(
        preservedBasketItems,
        sessionData.orderId,
        onlineModels,
        initialShoppingBasket.vouchers?.[0],
        analyticsPaymentType
      );
      setAnalyticsTracked(true);
    }
  }, [
    sessionData,
    preservedBasketItems,
    allExpandedModelsLoaded,
    onlineModels,
    analyticsTracked,
    initialShoppingBasket.vouchers,
  ]);

  if (!isAuthenticated || !authenticatedUser) {
    return null;
  }

  if (isLoading || !allExpandedModelsLoaded) {
    return <Loading big={true} />;
  }

  const contactInfo = getContactInfo(authenticatedUser);

  // If user reloads page after successful order (storage already cleared), show simple thank you message
  if (!sessionData) {
    return (
      <Grid colWidthL={8}>
        <h1>{t.O4R6('Thank you for your order!')}</h1>
        <p>
          {t.YMF4('You can track your order in OmaElisa')}{' '}
          <a href={paths.CUSTOMER_ORDER_HOME}>{t.GCO3(orderHistoryMsg)}</a>{' '}
          {t.YMF5('It may take a while for your order to appear.')}
        </p>
        <CL.Button
          className="of-order-info__return-button"
          color="primary"
          size="l"
          onClick={() => {
            handleReturnToShop();
          }}
        >
          {t.PLAW(returnToTheShopMsg)}
        </CL.Button>
        <OrderNewsLetter />
      </Grid>
    );
  }

  const deliveryType = sessionData?.formData.delivery.deliveryType;
  const deliveryInfo = deliveryType ? getDeliveryMethodEntry(deliveryType)?.description : undefined;
  const nothingToDeliver = deliveryType === DeliveryType.NONE;
  const deliveryAddress = sessionData?.formData.delivery.deliveryAddress;
  const deliveryFields = nothingToDeliver || !deliveryAddress ? [] : getDeliveryFields(deliveryAddress);
  const deliveryPrice = sessionData?.formData.delivery.deliveryPriceInCents;

  return (
    <Grid colWidthL={8}>
      <div className="of-place-order__thank-you">
        <h1>{t.O4R6('Thank you for your order!')}</h1>
        <p>{t.ZDQ2('We have sent an order confirmation to your email {}.', contactInfo.email)}</p>
        <p>
          {t.YMF4('You can track your order in OmaElisa')}{' '}
          <a href={paths.CUSTOMER_ORDER_HOME}>{t.GCO3(orderHistoryMsg)}</a>{' '}
          {t.YMF5('It may take a while for your order to appear.')}
        </p>
        <OrderInfo
          mainTitle={t.VDB5('Order summary')}
          orderContactInfo={{ fields: getContactInfoLines(contactInfo) }}
          deliveryInfo={{
            deliveryFields: deliveryFields,
            deliveryInfo: deliveryInfo ? (
              <>
                <h4>{t.GPPB('Delivery information')}</h4>
                <p>{deliveryInfo}</p>
              </>
            ) : undefined,
          }}
          gutter={false}
          headerLevel="h2"
          horizontalPadding={false}
          justifyCenter={false}
          showBorder={false}
        />
        <h3 className={dsClass.MARGIN_VERTICAL_0}>{t.IY4Z(productsMsg)}</h3>

        <ul className="ds-shoppingcart-wrapper">
          {shoppingBasket.items?.flatMap(basketItem => {
            const onlineModel = getOnlineModelForBasketItem(onlineModels, basketItem);
            const offer = getOfferForBasketItem(onlineModels, basketItem);
            if (!onlineModel || !offer) {
              return [];
            }

            const placeOrderItemUnits = sessionData.formData.products.placeOrderItemUnits ?? [];
            const addons = getAddonsForBasketItem(basketItem.id, onlineModel, basketItem, placeOrderItemUnits);
            const pcs = (n: number) => t.BDBD('{} pcs', String(n));

            return [
              <li key={basketItem.id}>
                <ProductEntry
                  basketItem={basketItem}
                  offer={offer}
                  onlineModel={onlineModel}
                  changeQuantity={() => {}}
                  campaignData={campaignData}
                  readOnly
                  allModels={onlineModels}
                />
              </li>,
              ...addons.map(addon => (
                <li key={addon.key}>
                  <ProductEntryView
                    productName={addon.name}
                    productImage={<div style={{ width: 64 }} />}
                    price={
                      <>
                        <span className={dsClass.SHOPPINGCART_PRICE_AMOUNT}>{addon.price.amount}</span>
                        <span className={dsClass.SHOPPINGCART_PRICE_UNIT}>{addon.price.unit}</span>
                      </>
                    }
                    disclaimerFields={<></>}
                    description={<span>{pcs(addon.quantity)}</span>}
                    readOnly
                  />
                </li>
              )),
            ];
          })}
          {deliveryType && !nothingToDeliver && (
            <li key="delivery">
              <ProductEntryView
                productName={getDeliveryMethodEntry(deliveryType)?.header ?? t.BSE3('Delivery')}
                productImage={<CL.Icon icon="delivery" size="xl" />}
                price={<>{formatSumToString(deliveryPrice ?? 0)}</>}
                disclaimerFields={<></>}
                description={<>{deliveryFields.join(', ')}</>}
              />
            </li>
          )}
        </ul>

        <ThankYouSummary
          shoppingBasket={shoppingBasket}
          onlineModels={onlineModels}
          campaignData={campaignData}
          sessionData={sessionData}
        ></ThankYouSummary>
        <CL.Button
          className={classNames(dsClass.MARGIN_VERTICAL_4)}
          color="primary"
          size="l"
          onClick={() => {
            handleReturnToShop();
          }}
        >
          {t.PLAW(returnToTheShopMsg)}
        </CL.Button>
      </div>
      <OrderNewsLetter />
    </Grid>
  );
};
